import { Injectable } from '@angular/core';

import { Unsubscribe, User } from 'firebase/auth';
import { addDoc, collection, connectFirestoreEmulator, deleteDoc, doc, documentId, getDoc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, QueryConstraint, QueryDocumentSnapshot, QueryLimitConstraint, QueryOrderByConstraint, QueryStartAtConstraint, serverTimestamp, setDoc, startAfter, startAt, updateDoc, where } from 'firebase/firestore';
import { Observable, Subscription } from 'rxjs';
import { BankAccount, Transaction } from 'bankapp-types';
import { SubscriptionPreferences } from 'src/class/classes';
import { GlobalsService } from './globals.service';
import { testing } from './testing'
import { MenuController, ModalController } from '@ionic/angular/standalone';
import { SubscribeComponent } from 'src/components/subscribe/subscribe.component';
import { UIService } from './ui.service';
import {unzip} from 'unzipit';
import { DatePipe } from '@angular/common';
import { LoadedNarrationIndex, MatchableEntry, NarrationIndexService } from './narration-index.service';

@Injectable({
    providedIn: 'root'
})
export class FirestoreService {

    private firestore = getFirestore()

    public bankAccountsList = collection(this.firestore, "bankAccounts");
    //public transactionsList: AngularFirestoreCollection;

    public bankAccountsLocal: BankAccount[] = []

    public currentBank: BankAccount

    public checkedAccountsLocal = []

    public transactions = []

    public previews = []

    public checkedTransactions = []

    public bankSubscription: Subscription = undefined

    public developer: boolean = false

    public preview: boolean = false

    public viewBpay: boolean = false

    constructor(
        private globalsService: GlobalsService,
        private menuController: MenuController,
        private modalController: ModalController,
        private uiService: UIService,
        private datePipe: DatePipe,
        public narrationIndex: NarrationIndexService
    ) {
        if (testing) connectFirestoreEmulator(this.firestore, "localhost", 8080)
    }

    watchBankAccounts() {
        if (this.bankSubscription) {
            this.bankSubscription.unsubscribe()
        }
        this.bankSubscription = this.bankAccountWatcher().subscribe(data => {
            this.bankAccountsLocal = data.map(account => new BankAccount(account))
            if (data.length != this.checkedAccountsLocal.length) {
                this.checkedAccountsLocal = new Array(data.length);
                for (let i = 0; i < data.length; i++) {
                    this.checkedAccountsLocal[i] = false
                }
            }
        })
    }

    bankAccountsUnsubscribe: Unsubscribe

    bankAccountWatcher() {
        return new Observable<any[]>((subscriber) => {
            if (this.bankAccountsUnsubscribe) this.bankAccountsUnsubscribe()
            this.bankAccountsUnsubscribe = onSnapshot(query(this.bankAccountsList, orderBy("important", "desc")), (snap) => {
                subscriber.next(snap.docs.map(doc => doc.data()))
            })
        })
    }

    async getBankAccount(bankId: string) {
        const bank = await getDoc(doc(this.firestore, `bankAccounts/${bankId}`))
        if (bank.exists()) {
            return new BankAccount(bank.data() as any)
        }
    }

    async updateBankAccount(bankAccount, payload) {
        if(!bankAccount) return
        return updateDoc(doc(this.bankAccountsList, bankAccount), payload)
    }


    transPerQuery = 100

    noMoreTransactions = false

    /**
     * An array of arrays storing each page of transactions
     */
    paginatedTransactions: Transaction[] = []

    /**
     * Every transaction paged in from Firestore, before the client side filters
     * are applied. Paging works off this rather than the filtered list.
     */
    allTransactions: Transaction[] = []

    /**
     * Narration substrings to match, OR'd together and case insensitive.
     * Firestore has no substring operator, so narration is matched here against
     * whatever has been paged in so far.
     */
    narrationFilters: string[] = []

    /**
     * Transactions the narration index says match, newest first. Null means the
     * search isn't index-backed and the fallback scan is in charge.
     */
    narrationMatches: MatchableEntry[] = null

    /** How far through narrationMatches the pager has got */
    narrationMatchCursor = 0

    /** Set once per filter application so scrolling doesn't re-resolve the match list */
    narrationSearchPrepared = false

    /** Surfaced to the UI so it can offer to build or rebuild the index */
    narrationIndexState: "none" | "fresh" | "stale" | "needsRebuild" = "none"

    lastIndex: number

    loading = false

    /**
     * Latched for the whole of a fallback scan. queryTransactions toggles loading
     * per page, which made the viewport unmount and remount once per page while a
     * filter was matching nothing.
     */
    scanning = false

    orderedBpay = {}

    queryParams = []

    async switchBpay(value: boolean) {
        const loading = await this.uiService.presentLoading("Switching Views (could take 1min+)...")
        const success = await this.waitForNotLoading()
        if(!success) {
            await loading.dismiss()
            await this.uiService.presentOKAlert("Uh Oh!", "There was an error switching the view due to the current view still loading.")
            return
        }
        await loading.dismiss()
        this.viewBpay = value
        this.resetTransactionQuery()
        this.queryTransactions()
    }

    waitForNotLoading(retries = 600) {
        return new Promise<boolean>((resolve, reject) => {
            let tries = 0
            const interval = setInterval(() => {
                if (!this.loading) {
                    clearInterval(interval)
                    resolve(true)
                }
                tries++
                if(tries > retries) {
                    clearInterval(interval)
                    resolve(false)
                }
            }, 100)
        })
    }


    resetTransactionQuery() {
        this.lastIndex = undefined
        this.noMoreTransactions = false
        this.checkedTransactions = []
        this.paginatedTransactions = []
        this.allTransactions = []
        this.narrationFilters = []
        this.narrationMatches = null
        this.narrationMatchCursor = 0
        this.narrationSearchPrepared = false
        this.queryParams = []
    }

    /** Queries transactions for the current bank account */
    async queryTransactions(bpay = this.viewBpay) {
        if (!this.currentBank || this.noMoreTransactions || this.loading) return

        try {
            console.log("actually loading more")
            this.loading = true
            const transCollection = collection(this.firestore, `bankAccounts/${this.currentBank.bankId}/${bpay ? 'bpay-' : ''}transactions`)
            if (!this.lastIndex) this.lastIndex = bpay ? this.currentBank.bpayTransactionCounter : this.currentBank.transactionCounter
            let queryConstraints: QueryConstraint[] = [orderBy("index", "desc"), startAfter(this.lastIndex), limit(this.transPerQuery)]

            if (this.queryParams.length) {
                queryConstraints = [...queryConstraints, ...this.queryParams.map(q => where(q.field, q.operator, q.value))]
            }

            const { docs } = await getDocs(query(transCollection, ...queryConstraints))
            if (docs.length < this.transPerQuery) this.noMoreTransactions = true
    
            const newT = docs.map(doc => { return new Transaction({ ...doc.data(), id: doc.id }) })
        
            this.allTransactions = [...this.allTransactions, ...newT].sort((a, b) => b.index - a.index)
            // Advance the cursor as soon as the page is in hand. If anything below
            // throws, the next call must move on rather than refetch this page.
            this.lastIndex = this.allTransactions[this.allTransactions.length - 1]?.index
            this.paginatedTransactions = this.filterNarration(this.allTransactions)
    
            if(this.currentBank.tags?.includes("Bpay") && !bpay) {
                const uniqueNewDates = [...new Set(newT.map(t => t.date))]

                await Promise.all(uniqueNewDates.map(async date => {
                    if(!this.orderedBpay[date]?.length) {
                        const bpayTransactions = await this.fetchTransactions(this.currentBank, true, [where("date", ">=", date - 43200000), where("date", "<=", date + 43200000)])
                        this.orderedBpay[date] = bpayTransactions
                    }
                }))
            }
        } catch(error) {
            console.error(error)
        } finally {
            this.loading = false
        }
    }

    /**
     * Narrows transactions to those whose narration contains any of the filter
     * strings. Narration is coerced rather than assumed to be a string: some
     * stored rows hold a number, which is why the template guards it with
     * narration.includes?.() too.
     */
    filterNarration(transactions: Transaction[]) {
        if (!this.narrationFilters.length) return transactions
        const needles = this.narrationFilters.map(n => String(n).toLowerCase())
        return transactions.filter(t => {
            const narration = String(t.narration ?? "").toLowerCase()
            return needles.some(needle => narration.includes(needle))
        })
    }

    /**
     * Matching rows to pull in per fallback load. At 29px a row this has to exceed
     * the tallest viewport, or the content never overflows, no scroll event fires
     * and loading stops for good. 200 rows clears a 4K display with room over.
     */
    filteredResultBuffer = 200

    /** where(documentId(), in, ...) takes at most 30 values, so match pages fan out into groups */
    private matchFetchGroupSize = 30

    /**
     * recon and tags are edited after a transaction is written, so they are
     * deliberately absent from the index. A search using them has to go through
     * the live query path instead.
     */
    private indexCanServe() {
        return this.queryParams.every(p => p.field === "date" || p.field === "transaction")
    }

    /** Re-checks the live row against the constraints the index answered from */
    private transactionMatchesQueryParams(transaction: Transaction) {
        return this.entryMatchesQueryParams({
            date: transaction.date,
            transaction: transaction.transaction
        } as MatchableEntry)
    }

    /** Applies the date and amount constraints that the index carries itself */
    private entryMatchesQueryParams(entry: MatchableEntry) {
        return this.queryParams.every(p => {
            const value = p.field === "date" ? entry.date : p.field === "transaction" ? entry.transaction : null
            if (value === null) return true
            if (p.operator === ">=") return value >= p.value
            if (p.operator === "<=") return value <= p.value
            return true
        })
    }

    /**
     * Resolves the narration search against the prebuilt index, leaving
     * narrationMatches null when there is no usable index so the caller falls
     * back to scanning. Transactions ingested since the build are searched live,
     * so an out-of-date index still returns complete results.
     */
    async prepareNarrationSearch() {
        this.narrationSearchPrepared = true
        this.narrationMatches = null
        this.narrationMatchCursor = 0
        this.narrationIndexState = "none"

        if (!this.currentBank || !this.narrationFilters.length) return

        let loaded: LoadedNarrationIndex
        try {
            loaded = await this.narrationIndex.load(this.currentBank.bankId, this.viewBpay)
        } catch (error) {
            console.error(error)
            return
        }
        if (!loaded) return

        // Reindexing moves entries between chunks, so a flagged index would return
        // the wrong rows rather than merely out-of-date ones
        if (loaded.needsRebuild) {
            this.narrationIndexState = "needsRebuild"
            return
        }

        const counter = this.viewBpay ? this.currentBank.bpayTransactionCounter : this.currentBank.transactionCounter
        const stale = this.narrationIndex.isStale(loaded, counter)
        this.narrationIndexState = stale ? "stale" : "fresh"

        if (!this.indexCanServe()) return

        let entries = loaded.entries

        if (stale) {
            // Anything written after the build is missing from the chunks, so pull
            // it live rather than quietly reporting fewer matches than exist
            const missing = await this.fetchTransactions(this.currentBank, this.viewBpay, [where("index", ">", loaded.coveredThrough)])
            const recovered: MatchableEntry[] = (missing ?? []).map(t => ({
                index: t.index,
                id: t.id,
                narration: t.narration ?? "",
                lower: (t.narration ?? "").toLowerCase(),
                date: t.date ?? 0,
                transaction: t.transaction ?? 0
            }))
            entries = [...recovered, ...entries].sort((a, b) => b.index - a.index)
        }

        this.narrationMatches = this.narrationIndex
            .matchNarration(entries, this.narrationFilters)
            .filter(entry => this.entryMatchesQueryParams(entry))
    }

    /** Total matches the index found, for the UI to report */
    get narrationMatchCount() {
        return this.narrationMatches?.length ?? null
    }

    /**
     * Fetches the next page of transactions named by the match list. The narration
     * filter is applied again to what comes back, so an entry whose narration has
     * changed since the index was built can't show up as a phantom match.
     */
    async queryMatchedTransactions() {
        if (!this.currentBank || this.noMoreTransactions || this.loading) return

        try {
            this.loading = true
            const slice = this.narrationMatches.slice(this.narrationMatchCursor, this.narrationMatchCursor + this.transPerQuery)
            if (!slice.length) {
                this.noMoreTransactions = true
                return
            }
            this.narrationMatchCursor += slice.length

            const transCollection = collection(this.firestore, `bankAccounts/${this.currentBank.bankId}/${this.viewBpay ? 'bpay-' : ''}transactions`)
            const ids = slice.map(e => e.id).filter(id => !!id)
            const groups: string[][] = []
            for (let i = 0; i < ids.length; i += this.matchFetchGroupSize) {
                groups.push(ids.slice(i, i + this.matchFetchGroupSize))
            }

            const pages = await Promise.all(groups.map(group => getDocs(query(transCollection, where(documentId(), "in", group)))))
            const newT = pages
                .flatMap(({ docs }) => docs.map(doc => new Transaction({ ...doc.data(), id: doc.id })))
                // The index only proposes; the live row decides, so an entry that has
                // since been edited can't show up as a match it no longer is
                .filter(t => this.transactionMatchesQueryParams(t))

            this.allTransactions = [...this.allTransactions, ...newT].sort((a, b) => b.index - a.index)
            this.paginatedTransactions = this.filterNarration(this.allTransactions)

            if (this.narrationMatchCursor >= this.narrationMatches.length) this.noMoreTransactions = true
        } catch (error) {
            console.error(error)
        } finally {
            this.loading = false
        }
    }

    /**
     * Queries transactions, then keeps paging while the narration filter is
     * leaving too few new rows to fill the view. Without this a page that matches
     * nothing leaves the viewport too short to scroll, so nothing more loads and
     * the user is stranded on an empty list.
     */
    async queryFilteredTransactions() {
        if (this.narrationFilters.length && !this.narrationSearchPrepared) {
            await this.prepareNarrationSearch()
        }

        // The index already knows every hit, so page straight through the match list
        if (this.narrationMatches) return this.queryMatchedTransactions()

        const target = this.paginatedTransactions.length + this.filteredResultBuffer

        this.scanning = true
        try {
            await this.queryTransactions()

            while (this.narrationFilters.length && !this.noMoreTransactions && this.paginatedTransactions.length < target) {
                const loadedBefore = this.allTransactions.length
                await this.queryTransactions()
                // A page that brought nothing new means we're stalled, so don't spin on it
                if (this.allTransactions.length === loadedBefore) break
            }
        } finally {
            this.scanning = false
        }
    }

    async fetchTransactions(bankAccount: BankAccount, bpay = this.viewBpay, queryConstraints: QueryConstraint[] = []) {
        if(!bankAccount) return
        const { docs } = await getDocs(query(collection(this.firestore, `bankAccounts/${bankAccount.bankId}/${bpay ? 'bpay-' : ''}transactions`), ...queryConstraints))
        return docs?.length ? docs.map(doc => new Transaction({ ...doc.data(), id: doc.id })) : []
    }

    async refreshTransactions() {
        this.resetTransactionQuery()
        this.currentBank = await this.getBankAccount(this.currentBank.bankId)
        await this.queryTransactions()
    }

    transactionsUnsubscribe: Unsubscribe

    transactionWatcher(bankAccount, bpay = this.viewBpay) {
        return new Observable<any[]>((subscriber) => {
            if (this.transactionsUnsubscribe) this.transactionsUnsubscribe()
            this.transactionsUnsubscribe = onSnapshot(collection(this.firestore, `bankAccounts/${bankAccount}/${bpay ? 'bpay-' : ''}transactions`), (snap) => {
                subscriber.next(snap.docs.map(doc => { return { ...doc.data(), ...{ id: doc.id } } }))
            })
        })
    }

    previewUnsubscribe: Unsubscribe

    previewWatcher(bankAccount) {
        return new Observable<any[]>((subscriber) => {
            if (this.previewUnsubscribe) this.previewUnsubscribe()
            this.previewUnsubscribe = onSnapshot(collection(this.firestore, `bankAccounts/${bankAccount}/preview`), (snap) => {
                subscriber.next(snap.docs.map(doc => doc.data()))
            })
        })
    }

    async getTransactions(bankAccount, queries = [],  bpay = this.viewBpay, batchSize = 200) {
        let transactions = [];
        let lastDoc = null;
        let moreTransactions = true;

        while (moreTransactions) {
            let queryConstraints: QueryConstraint[] = [orderBy("index", "desc"), limit(batchSize), ...queries];
            if (lastDoc) {
                queryConstraints.push(startAfter(lastDoc));
            }

            const { docs } = await getDocs(query(collection(this.firestore, `bankAccounts/${bankAccount}/${bpay ? 'bpay-' : ''}transactions`), ...queryConstraints));
            transactions = [...transactions, ...docs.map(doc => new Transaction({...doc.data(), id: doc.id }))];

            if (docs.length < batchSize) {
                moreTransactions = false;
            } else {
                lastDoc = docs[docs.length - 1];
            }
        }

        return transactions;
    }

    async getTransactionsAboveIndex(index: number) {
        return this.fetchTransactions(this.currentBank, this.viewBpay, [where("index", ">=", index)])
    }

    /** Fields the narration index stores, so an update only touches it when it has to */
    private touchesNarrationIndex(payload: any) {
        return payload && ("narration" in payload || "date" in payload || "transaction" in payload)
    }

    /**
     * Whether a payload carries every field an index entry needs. A partial write
     * would blank the fields it omits, and a blanked narration silently stops the
     * transaction being findable, so partial writes flag for rebuild instead.
     */
    private canRewriteIndexEntry(payload: any) {
        return typeof payload?.index === "number"
            && typeof payload?.narration === "string"
            && typeof payload?.date === "number"
            && typeof payload?.transaction === "number"
    }

    /**
     * maintainIndex is turned off by the bulk reindex paths, which shift thousands
     * of entries between chunks and flag the index for rebuild instead.
     */
    async updateTransaction(bankAccount, transaction, payload, bpay = this.viewBpay, maintainIndex = true) {
        const result = await updateDoc(doc(this.firestore, `bankAccounts/${bankAccount}/${bpay ? 'bpay-' : ''}transactions/${transaction}`), payload)

        if (maintainIndex && this.touchesNarrationIndex(payload)) {
            if (this.canRewriteIndexEntry(payload)) {
                await this.narrationIndex.upsert(bankAccount, bpay, [{ ...payload, id: transaction }])
            } else {
                // Not enough to rebuild the entry safely, so don't guess at it
                await this.narrationIndex.markNeedsRebuild(bankAccount, bpay)
            }
        }
        return result
    }

    async addTransaction(bankAccount, payload, bpay = this.viewBpay, maintainIndex = true) {
        console.log("we are adding", payload, bpay, bankAccount)
        const ref = await addDoc(collection(this.firestore, `bankAccounts/${bankAccount}/${bpay ? 'bpay-' : ''}transactions`), payload)

        if (maintainIndex && this.canRewriteIndexEntry(payload)) {
            await this.narrationIndex.upsert(bankAccount, bpay, [{ ...payload, id: ref.id }])
        }
        return ref
    }

    async deleteTransaction(bankId: string, transactionId: string, bpay = this.viewBpay, transactionIndex?: number) {
        const result = await deleteDoc(doc(this.firestore, `bankAccounts/${bankId}/${bpay ? 'bpay-' : ''}transactions/${transactionId}`))

        if (typeof transactionIndex === "number") {
            await this.narrationIndex.remove(bankId, bpay, [{ index: transactionIndex, id: transactionId }])
        }
        return result
    }

    async updateIndicesAbove(index: number, delta: number, bpay = this.viewBpay) {
        const transactions = await this.fetchTransactions(this.currentBank, bpay, [where("index", ">", index)])
        await Promise.all(transactions.map(t => this.updateTransaction(this.currentBank.bankId, t.id, { index: t.index + delta }, bpay, false)))
        await this.narrationIndex.markNeedsRebuild(this.currentBank.bankId, bpay)
        await this.updateBankAccount(this.currentBank.bankId, { [bpay ? 'bpayTransactionCounter' : 'transactionCounter']: this.currentBank[bpay ? 'bpayTransactionCounter' : 'transactionCounter'] + delta })
    }

    async reindexAndRebalanceAbove(index: number, updateBankBalance = false, bpay = this.viewBpay) {
        const transactions = await this.fetchTransactions(this.currentBank, bpay, [where("index", ">=", index), orderBy("index", "asc")])

        const firstTransaction = transactions.shift()

        if (!firstTransaction) return

        let runningBalance = firstTransaction.runningBalance

        await Promise.all(transactions.map((t, i) => {
            runningBalance += t.transaction
            return this.updateTransaction(this.currentBank.bankId, t.id, { index: firstTransaction.index + i + 1, runningBalance, ...(updateBankBalance ? {bankBalance: runningBalance} : {}) }, bpay, false)
        }))

        await this.narrationIndex.markNeedsRebuild(this.currentBank.bankId, bpay)

        await this.updateBankAccount(this.currentBank.bankId, { 
            [bpay ? 'bpayTransactionCounter' : 'transactionCounter']: firstTransaction.index + transactions.length + 1,
            ...(!bpay ? { runningBalance } : { bpayBalance: runningBalance }),
            ...(updateBankBalance && !bpay ? { bankBalance: runningBalance } : {}),
        })
    }

    /** Import transactions into an account (defaults to current account) */
    async importTransactions(transactions: Transaction[], bpay = this.viewBpay, bankId: string = this.currentBank?.bankId) {
        console.log("importing transactions")

        if (!(transactions?.length && bankId)) return

        const transactionsCollection = collection(this.firestore, `bankAccounts/${bankId}/${bpay ? 'bpay-' : ''}transactions`)

        //Delete every doc above the given lowest index provided
        const minIndex = transactions.reduce((min, t) => Math.min(min, t.index), transactions[0].index)
        const higherIndexTransactions = await this.getTransactions(bankId, [where("index", ">=", minIndex)])
        await Promise.all(higherIndexTransactions.map(t => deleteDoc(doc(this.firestore, `bankAccounts/${bankId}/${bpay ? 'bpay-' : ''}transactions/${t.id}`))))

        //Add all the new transactions
        const added = await Promise.all(transactions.map(transaction => addDoc(transactionsCollection, transaction.toJson())))

        //Keep the search index in step: drop the replaced range, add what took its place
        await this.narrationIndex.remove(bankId, bpay, higherIndexTransactions.map(t => ({ index: t.index, id: t.id })))
        await this.narrationIndex.upsert(bankId, bpay, transactions.map((t, i) => ({ ...t.toJson(), index: t.index, id: added[i].id })))

        //Updates the counts and balances
        const finalTransaction = transactions.sort((a, b) => a.index - b.index)[transactions.length - 1]

        const bankUpdate = this.viewBpay ? { bpayTransactionCounter: finalTransaction.index + 1, bpayBalance: finalTransaction.bankBalance } : {
            bankBalance: finalTransaction.bankBalance,
            runningBalance: finalTransaction.runningBalance,
            transactionCounter: finalTransaction.index + 1
        }

        await this.updateBankAccount(bankId, bankUpdate)

        console.log("done")
    }

    //Imports Bpay Reconciliation File (BRF)
    async importBrf(event: Event) {

        const loading = await this.uiService.presentLoading("Importing BRF...")

        try {
            let file = (event.target as HTMLInputElement).files[0]
            if(file.name.toLowerCase().endsWith(".zip")) {
                const {entries} = await unzip(file)
                const brfFile = Object.entries(entries).find(([key, val]) => key.toLowerCase().endsWith(".brf"))[1]
                file = new File([await brfFile.text()], brfFile.name, {type: "text/plain"})
            }
    
            const vals = await this.interpretBPAY(file)
    
            const existingDay = await this.fetchTransactions(this.currentBank, true, [where("date", "==", vals[0].date)])
    
            if(existingDay?.length) {
                await this.uiService.presentOKAlert("Existing Bpay Transactions", "There are already transactions for this day. Please delete them before importing new ones.")
                await loading.dismiss()
                return
            }
    
            const newerTransactions = (await this.fetchTransactions(this.currentBank, true, [where("date", ">", vals[0].date)]))?.sort((a,b) => a.index - b.index) ?? []
    
            const firstNewTransaction = newerTransactions?.length ? newerTransactions[0] : undefined
    
            const startingIndex = firstNewTransaction?.index ?? this.currentBank.bpayTransactionCounter
    
            let balance = newerTransactions?.length ? firstNewTransaction.bankBalance - firstNewTransaction.transaction : this.currentBank.bpayBalance
    
            const updatePackage = [...vals.map((val, index) => {
                balance += val.amount
                return new Transaction({
                    narration: val.BPAYRef,
                    transaction: val.amount,
                    date: val.date,
                    index: startingIndex + index,
                    type: "BPAY",
                    runningBalance: balance,
                    bankBalance: balance
                })
            }), ...(newerTransactions.map((t, i) => {
                balance += t.transaction
                return new Transaction({
                    ...t, 
                    index: startingIndex + vals.length + i,
                    runningBalance: balance,
                    bankBalance: balance
                })
            }) ?? [])]
            
            await Promise.all(updatePackage.map(transaction => transaction.id ? this.updateTransaction(this.currentBank.bankId, transaction.id, transaction.toJson(), true) : this.addTransaction(this.currentBank.bankId, transaction.toJson(), true)))
    
            await this.updateBankAccount(this.currentBank.bankId, {bpayTransactionCounter: startingIndex + updatePackage.length, bpayBalance: balance})
    
            await this.refreshTransactions()
        } catch(error) {
            console.error(error)
            await this.uiService.presentOKAlert("Uh Oh!", "There was an error importing the BRF. Please try again.")
        } finally {
            await loading.dismiss();
            (event.target as HTMLInputElement).value = ""
        }

    }

    async interpretBPAY(file: File) {
        let fileTotal = 0
        const fileAsText = await this.interpretTextFile(file)
        const splitFile = fileAsText.trim().split("\r\n")
        const date = this.interpretDate(splitFile[0].substring(47,55))
        const entries = []
        for(let i = 1 ; i < splitFile.length - 1 ; i++) {
          const line = splitFile[i]
          const customerRef = line.substring(12,21)
          const amount = +line.substring(79,91)/100
          entries.push({
            BPAYRef: customerRef,
            amount: amount,
            date: date
          })
          fileTotal += amount
        }
        return entries
      }

      interpretDate(dateString: string) {
        const year = +dateString.substring(0,4)
        const month = +dateString.substring(4,6) - 1
        const day = +dateString.substring(6,8)
        const timeZoneOffset = new Date().getTimezoneOffset() * 60 * 1000;
        let date = new Date(year, month, day).setHours(0,0,0,0) - timeZoneOffset - 8 * 60 * 60 * 1000
        return date
      }
    
      interpretTextFile(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
          let fileReader = new FileReader()
          fileReader.onloadend = () => {
            resolve(fileReader.result as string)
          }
          fileReader.readAsText(file)
        })
      }

      currencyFormat(num: number, dp: number = 2) {
        return (new Intl.NumberFormat('en-AU', { style: 'currency', currency: "AUD", maximumFractionDigits: dp, minimumFractionDigits: dp })).format(num)
      }

    //----------------------------- Duplicate Fixing -----------------------------

    async fixDuplicates() {
        const bankDocs = await getDocs(this.bankAccountsList)

        let done = 0

        let start = new Date()

        for (let bankAccount of bankDocs.docs) {
            console.log(bankAccount.id)

            await this.fixDup(bankAccount.id, bankAccount)

            done++
        }

        let finish = new Date()

        console.log("done all in", (finish.getTime() - start.getTime()))


    }

    async fixDup(bankId: string, bankAccountData?: QueryDocumentSnapshot) {
        const transCollection = collection(this.firestore, `bankAccounts/${bankId}/transactions`)

        const bankAccount = bankAccountData ?? (await getDoc(doc(this.firestore, `bankAccounts/${bankId}`)))

        const bankData = bankAccount.data()

        if (Math.abs(bankData.runningBalance - bankData.bankBalance) < 0.01) return

        let index = bankData.transactionCounter

        let isBroken = true

        console.log(index)

        const batchSize = 10

        let transactions = []

        do {
            console.log(index)
            const transBatch = await getDocs(query(transCollection, where("index", "<", index), orderBy("index", "desc"), limit(batchSize)))
            index -= transBatch.size
            const transactionData = transBatch.docs[0].data()
            console.log(transBatch.docs[0].data().index, transBatch.docs[transBatch.size - 1].data().index)
            isBroken = Math.abs(transactionData.bankBalance - transactionData.runningBalance) > 0.01
            transactions = [...transactions, ...transBatch.docs.map(doc => doc.data())]
        } while (isBroken && index > 0)

        console.log(transactions[0], transactions[transactions.length - 1])

        //fix errors

        const fixedTransactions = this.fixErrors(transactions.sort((a, b) => a.index - b.index))

        const brokenTransactions = await getDocs(query(transCollection, where("index", ">=", index)))
        await Promise.all(brokenTransactions.docs.map(doc => deleteDoc(doc.ref)))
        await this.narrationIndex.markNeedsRebuild(bankId, false)

        let runningBalance: number = fixedTransactions[0].bankBalance - fixedTransactions[0].transaction

        let finalTransactions = fixedTransactions.map((t: any, i: number) => {
            runningBalance += t.transaction

            return {
                ...t,
                runningBalance,
                index: i + index
            }
        })

        await Promise.all(finalTransactions.map(async (t: any, i: number) => {
            await addDoc(transCollection, t)
            console.log(`added transaction ${i} of ${fixedTransactions.length}`)
        }))

        const finalTransaction = finalTransactions[finalTransactions.length - 1]

        await this.updateBankAccount(bankId, {
            bankBalance: finalTransaction?.bankBalance ?? 0,
            runningBalance: finalTransaction?.runningBalance ?? 0,
            transactionCounter: (finalTransaction?.index ?? 0) + 1
        })
    }

    fixErrors(transactions: Array<any>): any {
        console.log(`Fixing errors in ${transactions.length}`)

        const fixedTransactions = [transactions.shift()]

        while (transactions.length) {
            console.log(transactions.length, fixedTransactions.length)
            const transaction = transactions[0]

            const previousTransaction = fixedTransactions[fixedTransactions.length - 1]

            const expectedBalance = previousTransaction.bankBalance + transaction.transaction

            if (Math.abs(expectedBalance - transaction.bankBalance) > 0.01) {
                console.log("broken transaction", transaction.index)

                const nextTransactionIndex = transactions.findIndex(t => Math.abs(previousTransaction.bankBalance + t.transaction - t.bankBalance) < 0.01)

                if (nextTransactionIndex >= 0) {
                    console.log("found next transaction", transaction.index, nextTransactionIndex, transactions.length)
                    const [nextTransaction] = transactions.splice(nextTransactionIndex, 1)

                    fixedTransactions.push(nextTransaction)
                } else {
                    console.error("aight I'm out", transactions.length)
                    break
                }
            } else {
                transactions.shift()
                const nextValidTransaction = transactions.findIndex(t => Math.abs(transaction.bankBalance + t.transaction - t.bankBalance) < 0.01)
                if (!transactions.length || nextValidTransaction >= 0) {
                    fixedTransactions.push(transaction)
                }
            }

        }

        return fixedTransactions
    }


    //-----------------------------------------------------------User Accounts-----------------------------------------------------------


    /**
     * Gets the user account from the provided UID
     * @param uid the user's UID
     */
    async getUserAccount(uid: string) {
        return (await getDoc(doc(this.firestore, `users/${uid}`))).data()
    }

    /**
     * Creates the user account for the provided user
     * @param user the firebase user for which to create the account
     */
    createUserAccount(user: User) {
        const newUserPack = {
            email: user.email
        }

        return setDoc(doc(this.firestore, `users/${user.uid}`), newUserPack) ?? null
    }


    async saveSubscriptionPrefs(prefs: SubscriptionPreferences, accounts: Array<BankAccount>) {
        const prefObject = Object.fromEntries(Object.entries(prefs).map(([key, val]) => [key, val.checked]))
        const user = this.globalsService.authSubject.value
        if (!(user && prefObject)) return



        await Promise.all(accounts.map(async bankAccount => {
            bankAccount.subscriptions ??= {}
            bankAccount.subscriptions[user.uid] = prefObject
            await setDoc(doc(this.firestore, `bankAccounts/${bankAccount.bankId}`), bankAccount.toObject(), { merge: true })
        }))
    }


    //-----------------------------------------------------------Util-----------------------------------------------------------

    async subscribeAccount(menuName?: string, bankAccount?: any) {
        if (menuName) await this.menuController.close(menuName)
        const modal = await this.modalController.create({
            component: SubscribeComponent,
            cssClass: "transparent-modal",
            componentProps: { bankAccounts: bankAccount ? [bankAccount] : this.bankAccountsLocal.filter((x, i) => this.checkedAccountsLocal[i]) }
        })

        await modal.present()
    }

    addOCLUpdate(update: any) {
        return addDoc(collection(this.firestore, "oclUpdates"), { ...update, time: serverTimestamp() })
    }

    updates

    async getOCLUpdates() {
        const { docs } = await getDocs(query(collection(this.firestore, "oclUpdates"), where("status", "!=", "completed")))
        this.updates = docs.map(doc => ({...doc.data(), id: doc.id}))
    }
}
